import {useEffect} from 'react';

/**
 * Promotes a portalled floating element (popover, context menu, tooltip, hover
 * card, etc.) into the browser's "top layer" - the same rendering layer used
 * by native <dialog> elements opened via showModal().
 *
 * Top-layer elements always paint above regular DOM content regardless of
 * z-index, and are stacked amongst themselves by show order (most recently
 * shown on top). This guarantees floating UI is never visually trapped behind
 * a modal dialog, without needing to know where the dialog is or reach into
 * its DOM (e.g. anchor.closest('dialog')).
 *
 * Relies on the Popover API (`popover` attribute + showPopover()/hidePopover()).
 * '@oddbird/popover-polyfill' is imported once at app bootstrap (src/index.tsx)
 * so this works identically on browsers without native support.
 */
export function useTopLayer(element: HTMLElement | null): void {
  useEffect(
    () => {
      if (!element) {
        return undefined;
      }

      if (!element.hasAttribute('popover')) {
        element.setAttribute('popover', 'manual');
      }

      try {
        element.showPopover();
      } catch {
        // Already open (e.g. a fast re-render re-ran this effect) - safe to ignore.
      }

      return () => {
        try {
          element.hidePopover();
        } catch {
          // Already closed/removed from the document - safe to ignore.
        }
      };
    },
    [element]
  );
}
